import React, { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import { CreditCard, Upload, Award, History, FileText, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { debtorService, analyticsService, API_BASE } from '@/lib/api';
import NegotiationModule from '@/components/NegotiationModule';
import WhatsAppBubble from '@/components/WhatsAppBubble';
import { normalizeDateStr, isDiscountExpired } from '@/components/debtor-portal/dateHelpers';

// Layout components (eager - needed immediately)
import DesktopLayout from '@/components/debtor-portal/DesktopLayout';
import MobileLayout from '@/components/debtor-portal/MobileLayout';

// Modal components (lazy - loaded on demand)
const ChatModal = lazy(() => import('@/components/debtor-portal/ChatModal'));
const CertificateModal = lazy(() => import('@/components/debtor-portal/CertificateModal'));
const PaymentReportModal = lazy(() => import('@/components/debtor-portal/PaymentReportModal'));
const DocumentModal = lazy(() => import('@/components/debtor-portal/DocumentModal'));
const HistoryModal = lazy(() => import('@/components/debtor-portal/HistoryModal'));
const PaymentOptionsModal = lazy(() => import('@/components/debtor-portal/PaymentOptionsModal'));
const DocumentRequestModal = lazy(() => import('@/components/debtor-portal/DocumentRequestModal'));
const DescargoModal = lazy(() => import('@/components/debtor-portal/DescargoModals').then(m => ({ default: m.DescargoModal })));
const DescargoHistoryModal = lazy(() => import('@/components/debtor-portal/DescargoModals').then(m => ({ default: m.DescargoHistoryModal })));

const DebtorDashboard = () => {
  const { token } = useParams();

  // Core data
  const [debtor, setDebtor] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portfolio, setPortfolio] = useState(null);
  const [visibleFields, setVisibleFields] = useState([]);
  const [documentInfo, setDocumentInfo] = useState(null);
  const [requests, setRequests] = useState([]);

  // UI state
  const [showBalance, setShowBalance] = useState(true);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedActas, setSelectedActas] = useState([]);

  // Modal open states
  const [showChat, setShowChat] = useState(false);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [showDocRequestModal, setShowDocRequestModal] = useState(false);
  const [showDescargoModal, setShowDescargoModal] = useState(false);
  const [showDescargoHistory, setShowDescargoHistory] = useState(false);
  const [clientDescargos, setClientDescargos] = useState([]);

  // Exit offer
  const [showExitOffer, setShowExitOffer] = useState(false);
  const [exitOfferShown, setExitOfferShown] = useState(false);

  // Analytics tracking
  const trackAction = useCallback((action, section, metadata = null) => {
    if (token) analyticsService.trackAction(token, action, section, metadata).catch(() => {});
  }, [token]);

  // Data loading
  useEffect(() => {
    loadDebtorData();
    loadRequests();
    trackAction('view', 'inicio');
    const handleBeforeUnload = (e) => {
      if (!exitOfferShown && tenant?.enable_refinancing) {
        e.preventDefault();
        e.returnValue = '';
        setShowExitOffer(true);
        setExitOfferShown(true);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exitOfferShown, tenant]);

  useEffect(() => {
    if (token) loadClientDescargos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadDebtorData = async () => {
    try {
      const [debtorResponse, invoicesResponse] = await Promise.all([
        debtorService.getInfo(token),
        debtorService.getInvoices(token)
      ]);
      setDebtor(debtorResponse.data.debtor);
      setTenant(debtorResponse.data.tenant);
      setPortfolio(debtorResponse.data.portfolio);
      setVisibleFields(debtorResponse.data.visible_fields || []);
      setDocumentInfo(debtorResponse.data.document || null);
    } catch { toast.error('Error al cargar la informacion'); }
    finally { setLoading(false); }
  };

  const loadRequests = async () => {
    try { const response = await debtorService.getRequests(token); setRequests(response.data || []); } catch (e) { console.error('Error loading requests:', e); }
  };

  const loadClientDescargos = async () => {
    try {
      const res = await fetch(`${API_BASE}/debtor/${token}/descargos`);
      if (res.ok) setClientDescargos(await res.json());
    } catch (e) { console.error('Error loading descargos:', e); }
  };

  // Payment handlers
  const handleMercadoPagoPayment = async (payAmount, actaNumbers = []) => {
    try {
      toast.loading('Creando link de pago...');
      const response = await debtorService.createPayment(token, payAmount, actaNumbers);
      toast.dismiss();
      if (response.data.success && response.data.init_point) {
        window.location.href = response.data.init_point;
      } else { toast.error('Error al generar el link de pago'); }
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || 'Error al iniciar el pago');
    }
  };

  // Acta selection
  const toggleActa = (actaNumber) => {
    if (actaNumber === '__all__') {
      setSelectedActas(prev => prev.length === pendingActas.length ? [] : pendingActas.map(a => a.number));
      return;
    }
    setSelectedActas(prev => prev.includes(actaNumber) ? prev.filter(n => n !== actaNumber) : [...prev, actaNumber]);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f5f5f5' }}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-t-teal-500 border-slate-200 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  // Computed values - calculate from actas, not debtor-level fields
  const primaryColor = tenant?.primary_color || '#0d9488';

  const allowPartialActaPayment = portfolio?.allow_partial_acta_payment || false;
  const allActas = (debtor?.domains || []).flatMap(d => (d.actas || []).filter(a => a.number));
  const pendingActas = allActas.filter(a => a.status !== 'paid');
  const hasActaAmounts = pendingActas.some(a => (a.monto || a.monto_quita || 0) > 0);
  const canSelectActas = allowPartialActaPayment && hasActaAmounts && pendingActas.length > 1;

  // Recalculate totals from actas when acta-level amounts exist
  let totalDebt, discountAmount, totalDebtWithFees;
  if (hasActaAmounts) {
    // effective amount per acta: monto if available, otherwise monto_quita
    totalDebt = pendingActas.reduce((sum, a) => sum + (a.monto || a.monto_quita || 0), 0);
    // Monto con Quita = per-acta: if quita NOT expired and both amounts exist, use quita; else use effective
    const quitaTotal = pendingActas.reduce((sum, a) => {
      const discExpired = isDiscountExpired(a.discount_expiry);
      // Fallback: if no discount_expiry but monto is expired, quita also expired
      const montoExpired = isDiscountExpired(a.monto_expiry);
      const expired = discExpired || (!a.discount_expiry && montoExpired);
      const effective = a.monto || a.monto_quita || 0;
      if (!expired && a.monto_quita > 0 && a.monto > 0) return sum + a.monto_quita;
      return sum + effective;
    }, 0);
    discountAmount = quitaTotal < totalDebt ? quitaTotal : null;
    totalDebtWithFees = totalDebt + (debtor?.fees_amount || 0);
  } else {
    // Fallback to debtor-level fields when no acta amounts
    totalDebt = debtor?.total_debt || 0;
    discountAmount = debtor?.discount_amount;
    totalDebtWithFees = debtor?.total_debt_with_fees || totalDebt;
  }
  const feesAmount = debtor?.fees_amount || 0;

  let amountToPay;
  if (canSelectActas && selectedActas.length > 0) {
    const selectedActaObjects = pendingActas.filter(a => selectedActas.includes(a.number));
    amountToPay = selectedActaObjects.reduce((sum, a) => {
      const discExpired = isDiscountExpired(a.discount_expiry);
      const montoExpired = isDiscountExpired(a.monto_expiry);
      const expired = discExpired || (!a.discount_expiry && montoExpired);
      const effective = a.monto || a.monto_quita || 0;
      if (!expired && a.monto_quita > 0 && a.monto > 0) return sum + a.monto_quita;
      return sum + effective;
    }, 0);
  } else {
    amountToPay = (discountAmount != null && discountAmount > 0) ? discountAmount : totalDebtWithFees;
  }

  const computeNearestExpiry = (field) => {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    let nearest = null;
    for (const a of pendingActas) {
      if (!a[field]) continue;
      const dateStr = normalizeDateStr(a[field]);
      if (dateStr && dateStr >= todayStr && (!nearest || dateStr < nearest)) nearest = dateStr;
    }
    return nearest;
  };
  const nearestDiscountExpiry = computeNearestExpiry('discount_expiry');
  const nearestMontoExpiry = computeNearestExpiry('monto_expiry');

  const descargoConfig = tenant?.descargo_config || { enabled: false, causes: [] };

  // Pay Now handler (defined after amountToPay is computed)
  const doPayNow = () => {
    const hasMpApi = !!tenant?.mercadopago_credentials?.access_token;
    const hasMpQr = !!tenant?.mp_manual_qr_url && !hasMpApi;
    const hasTransfer = tenant?.payment_methods?.transfer && tenant?.bank_accounts?.length > 0;
    const availableMethods = [hasMpApi, hasMpQr, hasTransfer].filter(Boolean).length;
    if (availableMethods === 1 && hasMpApi) {
      handleMercadoPagoPayment(amountToPay);
    } else if (availableMethods >= 1) {
      setShowPaymentOptions(true);
    }
  };

  // Quick actions
  const quickActions = [
    { id: 'pay', icon: CreditCard, label: 'Pagar Ahora', color: '#10b981', onClick: () => { trackAction('click', 'pagar'); doPayNow(); } },
    { id: 'report', icon: Upload, label: 'Reportar Pago', color: '#3b82f6', onClick: () => { trackAction('click', 'reportar_pago'); setShowPaymentModal(true); } },
    { id: 'certificate', icon: Award, label: 'Libre Deuda', color: '#8b5cf6', onClick: () => { trackAction('click', 'libre_deuda'); setShowCertificateModal(true); } },
    { id: 'history', icon: History, label: 'Mis Solicitudes', color: '#f59e0b', onClick: () => { trackAction('click', 'solicitudes'); setShowHistoryModal(true); }, badge: requests.filter(r => r.status === 'pending').length || null }
  ];
  if (documentInfo) {
    quickActions.splice(2, 0, { id: 'document', icon: FileText, label: documentInfo.type, color: '#06b6d4', onClick: () => { trackAction('click', 'documento'); setShowDocumentModal(true); } });
  }
  if (portfolio?.enable_document_requests) {
    quickActions.push({ id: 'doc-request', icon: FileText, label: 'Solicitar Acta/Factura', color: '#6366f1', onClick: () => { trackAction('click', 'solicitar_documento'); setShowDocRequestModal(true); } });
  }
  if (descargoConfig.enabled) {
    quickActions.push({ id: 'descargo', icon: Shield, label: 'Descargo', color: '#8b5cf6', onClick: () => { trackAction('click', 'descargo'); setShowDescargoModal(true); } });
  }

  // Shared props for layouts
  const layoutProps = {
    debtor, tenant, portfolio, primaryColor, documentInfo, visibleFields,
    totalDebt, totalDebtWithFees, discountAmount, feesAmount, amountToPay,
    nearestMontoExpiry, nearestDiscountExpiry,
    allowPartialActaPayment, hasActaAmounts, pendingActas, canSelectActas,
    showBalance, setShowBalance, showDetail, setShowDetail,
    selectedActas, toggleActa, quickActions, onPayNow: doPayNow,
  };

  return (
    <>
      <DesktopLayout {...layoutProps} onShowDocument={() => setShowDocumentModal(true)} />
      <MobileLayout {...layoutProps} descargoConfig={descargoConfig} onShowChat={() => setShowChat(true)} onShowDescargo={() => setShowDescargoModal(true)} />

      {/* Lazy-loaded Modals - only downloaded when opened */}
      <Suspense fallback={null}>
        {showChat && <ChatModal open={showChat} onOpenChange={setShowChat} primaryColor={primaryColor} token={token} />}
        {showCertificateModal && <CertificateModal open={showCertificateModal} onOpenChange={setShowCertificateModal} primaryColor={primaryColor} totalDebtWithFees={totalDebtWithFees} token={token} onSuccess={loadRequests} />}
        {showPaymentModal && <PaymentReportModal open={showPaymentModal} onOpenChange={setShowPaymentModal} primaryColor={primaryColor} token={token} onSuccess={loadRequests} actas={allActas} />}
        {showDocumentModal && <DocumentModal open={showDocumentModal} onOpenChange={setShowDocumentModal} primaryColor={primaryColor} documentInfo={documentInfo} />}
        {showHistoryModal && <HistoryModal open={showHistoryModal} onOpenChange={setShowHistoryModal} primaryColor={primaryColor} requests={requests} />}
        {showPaymentOptions && <PaymentOptionsModal open={showPaymentOptions} onOpenChange={setShowPaymentOptions} primaryColor={primaryColor} tenant={tenant} amountToPay={amountToPay} discountAmount={discountAmount} trackAction={trackAction} onMercadoPagoPayment={() => handleMercadoPagoPayment(amountToPay, canSelectActas && selectedActas.length > 0 ? selectedActas : [])} />}
        {showDocRequestModal && <DocumentRequestModal open={showDocRequestModal} onOpenChange={setShowDocRequestModal} primaryColor={primaryColor} token={token} debtorId={debtor?.id} />}
        {showDescargoModal && <DescargoModal open={showDescargoModal} onOpenChange={setShowDescargoModal} primaryColor={primaryColor} token={token} debtor={debtor} descargoConfig={descargoConfig} descargoCount={clientDescargos.length} onViewHistory={() => { setShowDescargoModal(false); setShowDescargoHistory(true); }} onSuccess={loadClientDescargos} />}
        {showDescargoHistory && <DescargoHistoryModal open={showDescargoHistory} onOpenChange={setShowDescargoHistory} clientDescargos={clientDescargos} />}
      </Suspense>

      {/* Negotiation Module */}
      {tenant?.enable_refinancing && showExitOffer && (
        <NegotiationModule token={token} debtAmount={totalDebtWithFees} onClose={() => setShowExitOffer(false)} primaryColor={primaryColor} />
      )}

      {/* WhatsApp Bubble */}
      {tenant?.whatsapp_bubble_enabled && tenant?.whatsapp_bubble_number && (
        <WhatsAppBubble phoneNumber={tenant.whatsapp_bubble_number} message={tenant.whatsapp_bubble_message} />
      )}
    </>
  );
};

export default DebtorDashboard;
